import { CircularProgress, Dialog, Divider, MenuItem } from '@material-ui/core';
import { useFormikEnhanced } from '@superdispatch/forms';
import { Inline, useSnackbarStack } from '@superdispatch/ui';
import { logInfo } from 'shared/helpers/Logger';
import {
  composeSavedSearchTitle,
  PostingSearchCriteriaDTO,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import styled from 'styled-components';
import { trackLoadboardEvent } from '../core/LoadboardAnalytics';
import { usePostingsPageContext } from '../core/postings/PostingsPageContext';
import {
  AlertDuration,
  alertDurationOptions,
  formatAlertCreateMessage,
  PostingSavedSearchDTO,
  PostingSavedSearchPayloadDTO,
  postingSavedSearchPayloadSchema,
} from '../data/dto/PostingSavedSearchDTO';
import { LoadSearchSource } from '../data/dto/PostingsPageParamsDTO';
import { useCreateSavedSearch } from '../data/LoadboardSavedSearchesAPI';

const StyledMenuItem = styled(MenuItem)`
  padding: 6px 16px;
  min-height: unset;
`;

const Styled3 = styled.div`
  padding: 8px 0;
  width: 312px;
`;
const Styled4 = styled(Divider)`
  margin: 8px 0;
`;

interface Props {
  open: boolean;
  onClose: () => void;
  onCustomizeClick: () => void;
  source: LoadSearchSource | undefined;
  searchCriteria?: PostingSearchCriteriaDTO;
  onSuccess: (response: PostingSavedSearchDTO) => void;
}

export function LoadAlertDialog({
  open,
  source,
  searchCriteria,
  onCustomizeClick,
  onClose,
  onSuccess,
}: Props) {
  const { addSnackbar } = useSnackbarStack();
  const { params } = usePostingsPageContext();
  const criteria = searchCriteria || params.search_criteria;

  const { mutateAsync: createSavedSearch } = useCreateSavedSearch();

  const { handleSubmit, isSubmitting, setValues } = useFormikEnhanced<
    { alert_duration: AlertDuration },
    PostingSavedSearchDTO
  >({
    initialValues: { alert_duration: 'week' },
    onSubmit: ({ alert_duration }) => {
      const payload = postingSavedSearchPayloadSchema.cast({
        alert_duration,
        alert_enabled: true,
        search_criteria: criteria,
        description: composeSavedSearchTitle(criteria),
      }) as PostingSavedSearchPayloadDTO;

      logInfo('Create saved search', { payload });
      return createSavedSearch(payload);
    },
    onSubmitSuccess(response, { alert_duration }) {
      trackLoadboardEvent({
        name: 'SLB: Created Load Alert',
        source,
        savedSearch: response,
      });

      addSnackbar(formatAlertCreateMessage(alert_duration));
      onSuccess(response);
    },
    onSubmitFailure({ message }) {
      addSnackbar(message, { variant: 'error' });
    },
  });

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose}>
      <Styled3>
        {isSubmitting ? (
          <Inline>
            <CircularProgress size={16} />
          </Inline>
        ) : (
          <>
            <StyledMenuItem disabled={true}>Alert Duration</StyledMenuItem>

            {Object.entries(alertDurationOptions).map(([value, label]) => (
              <StyledMenuItem
                key={value}
                onClick={() => {
                  void setValues({ alert_duration: value as AlertDuration });
                  handleSubmit();
                }}
              >
                {label}
              </StyledMenuItem>
            ))}

            <Styled4 />

            <StyledMenuItem onClick={onCustomizeClick}>
              Customize Load Alert
            </StyledMenuItem>
          </>
        )}
      </Styled3>
    </Dialog>
  );
}
